import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DotsSixVertical, ArrowsClockwise, DownloadSimple, UploadSimple, CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import {
  BlockAvancement, BlockTableau, BlockCollaborateurs, BlockVigilance, BlockGraphique, BlockPodium, BlockChampions,
  STATUS_STYLE, statusOf, applyThresholds,
} from "./PerfProposalBlocks";

const BLOCK_META = {
  avancement: "Avancement vs objectif",
  podium: "Podium — trio de tête",
  champions: "Champions par service",
  classement: "Classement des collaborateurs",
  tableau: "Détail par service et indicateur",
  graphique: "Évolution 14 derniers jours",
  vigilance: "Vigilance — retours, litiges, SAV",
};

const DEFAULT_BLOCKS = [
  { id: "avancement", span: 2 },
  { id: "podium", span: 1 },
  { id: "champions", span: 2 },
  { id: "classement", span: 1 },
  { id: "tableau", span: 3 },
  { id: "graphique", span: 2 },
  { id: "vigilance", span: 1 },
];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const frDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PERIOD_WORD = { day: "jour", week: "semaine", month: "mois" };

// Classe des collaborateurs d'un ensemble de lignes : total, valeur précédente et mouvement de place
const rankRows = (rows, serviceName) => {
  const all = rows.map((r) => ({
    collaborator_id: r.collaborator_id,
    name: r.name,
    service_name: serviceName[r.service_id] || "—",
    total: Object.values(r.values).reduce((a, b) => a + (b || 0), 0),
    previous: Object.values(r.previous_values || {}).reduce((a, b) => a + (b || 0), 0) || null,
  }));
  const prevRank = {};
  all.filter((r) => r.previous).sort((a, b) => b.previous - a.previous)
    .forEach((r, i) => { prevRank[r.collaborator_id] = i + 1; });
  return all
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total)
    .map((r, i) => ({
      ...r,
      rank: i + 1,
      previous_rank: prevRank[r.collaborator_id] ?? null,
      delta_pct: r.previous ? Math.round(((r.total - r.previous) / r.previous) * 1000) / 10 : null,
      share_pct: r.previous ? Math.round((r.total / r.previous) * 100) : null,
    }));
};
const PERIOD_LABEL_TITLE = { day: "du jour", week: "de la semaine", month: "du mois" };

export default function PerformanceProposal() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [date, setDate] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [day, setDay] = useState(null);
  const [collab, setCollab] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendMetric, setTrendMetric] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
  const [layoutSource, setLayoutSource] = useState("builtin");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thresholds, setThresholds] = useState({ green_min: 90, amber_min: 50, down_amber_max: 150 });

  useEffect(() => {
    apiClient.get("/performance/settings")
      .then(({ data }) => { applyThresholds(data.thresholds); setThresholds(data.thresholds); })
      .catch(() => {});
  }, []);

  // Navigation : feuilleter les périodes passées sans toucher au sélecteur de date
  const navStep = (dir) => {
    const cur = parseISO(date);
    const today = parseISO(todayISO());
    if (period === "month") {
      const first = new Date(cur.getFullYear(), cur.getMonth() + dir, 1);
      if (first > new Date(today.getFullYear(), today.getMonth(), 1)) return null;
      const isCurrentMonth = first.getFullYear() === today.getFullYear() && first.getMonth() === today.getMonth();
      const target = isCurrentMonth ? today : new Date(first.getFullYear(), first.getMonth() + 1, 0);
      const label = first.toLocaleDateString("fr-FR", {
        month: "long",
        ...(first.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
      });
      return { iso: toISO(target), label };
    }
    if (period === "week") {
      const monday = new Date(cur);
      monday.setDate(monday.getDate() - ((cur.getDay() + 6) % 7));
      monday.setDate(monday.getDate() + dir * 7);
      if (monday > today) return null;
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const target = sunday > today ? today : sunday;
      return { iso: toISO(target), label: `semaine du ${monday.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}` };
    }
    // Jour : on saute les samedis et dimanches
    const next = new Date(cur);
    do {
      next.setDate(next.getDate() + dir);
    } while (next.getDay() === 0 || next.getDay() === 6);
    if (next > today) return null;
    return { iso: toISO(next), label: next.toLocaleDateString("fr-FR", { day: "2-digit", month: "long" }) };
  };
  const prevNav = navStep(-1);
  const nextNav = navStep(1);
  const atCurrent = !nextNav;

  useEffect(() => {
    apiClient.get("/performance/layout")
      .then(({ data }) => { setBlocks(data.blocks?.length ? data.blocks : DEFAULT_BLOCKS); setLayoutSource(data.source); })
      .catch(() => setBlocks(DEFAULT_BLOCKS));
  }, []);

  const persist = async (next) => {
    setBlocks(next);
    try {
      await apiClient.put("/performance/layout/me", { blocks: next.map((b) => ({ id: b.id, span: b.span })) });
      setLayoutSource("user");
    } catch {
      toast.error("La disposition n'a pas pu être enregistrée");
    }
  };

  const resetLayout = async () => {
    try {
      const { data } = await apiClient.delete("/performance/layout/me");
      setBlocks(data.blocks?.length ? data.blocks : DEFAULT_BLOCKS);
      setLayoutSource(data.source);
      toast.success("Disposition par défaut restaurée");
    } catch { toast.error("Erreur"); }
  };

  const publishLayout = async () => {
    try {
      await apiClient.put("/performance/layout/default", { blocks: blocks.map((b) => ({ id: b.id, span: b.span })) });
      toast.success("Disposition publiée pour tous les collaborateurs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d, c] = await Promise.all([
        apiClient.get("/performance/summary", { params: { period, date } }),
        apiClient.get("/performance/summary", { params: { period: "day", date } }),
        apiClient.get("/performance/summary/collaborators", { params: { period, date } }),
      ]);
      setSummary(m.data);
      setDay(d.data);
      setCollab(c.data);
      setTrendMetric((p) => p || m.data.metrics.find((x) => x.target_raw)?.metric_id || m.data.metrics[0]?.metric_id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [period, date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!trendMetric) return;
    apiClient.get("/performance/trend", { params: { metric_ids: trendMetric, days: 14, date } })
      .then(({ data }) => setTrend(data))
      .catch(() => setTrend(null));
  }, [trendMetric, date]);

  const serviceName = useMemo(() => Object.fromEntries((summary?.services || []).map((s) => [s.id, s.name])), [summary]);
  const todayValue = useMemo(() => Object.fromEntries((day?.metrics || []).map((m) => [m.metric_id, m.value])), [day]);

  const workdays = summary?.workdays || { done: 1, total: 1, left: 0 };

  const metrics = useMemo(() => {
    if (!summary) return [];
    return summary.metrics
      .filter((m) => serviceFilter === "all" || m.service_id === serviceFilter)
      .map((m) => ({ ...m, service_name: serviceName[m.service_id] || "—", today: todayValue[m.metric_id] ?? null }));
  }, [summary, serviceFilter, serviceName, todayValue]);

  const serviceStatus = useMemo(() => {
    if (!summary) return [];
    return summary.services.map((s) => {
      const ms = metrics.filter((m) => m.service_id === s.id);
      const states = ms.map((m) => statusOf(m.value, m.expected_to_date, m.direction)).filter(Boolean);
      const status = states.includes("rose") ? "rose" : states.includes("amber") ? "amber" : states.length ? "green" : "none";
      return { ...s, status, counted: states.length, ok: states.filter((x) => x === "green").length };
    });
  }, [summary, metrics, thresholds]);

  const ranking = useMemo(() => {
    if (!collab) return [];
    return rankRows(collab.rows.filter((r) => serviceFilter === "all" || r.service_id === serviceFilter), serviceName).slice(0, 8);
  }, [collab, serviceName, serviceFilter]);

  const championsByService = useMemo(() => {
    if (!collab || !summary) return [];
    return summary.services
      .filter((s) => serviceFilter === "all" || s.id === serviceFilter)
      .map((s) => ({ ...s, top: rankRows(collab.rows.filter((r) => r.service_id === s.id), serviceName).slice(0, 3) }))
      .filter((s) => s.top.length > 0);
  }, [collab, summary, serviceName, serviceFilter]);

  const trendMeta = trend?.metrics?.[0];
  const trendTargetDaily = useMemo(() => {
    const m = metrics.find((x) => x.metric_id === trendMetric);
    if (!m || m.target == null) return null;
    if (m.is_threshold) return m.target;
    return Math.round(m.target / Math.max(workdays.total, 1));
  }, [metrics, trendMetric, workdays]);

  useEffect(() => {
    if (!metrics.length) return;
    if (!metrics.some((m) => m.metric_id === trendMetric)) {
      setTrendMetric(metrics.find((m) => m.target_raw)?.metric_id || metrics[0].metric_id);
    }
  }, [metrics, trendMetric]);

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return setOverId(null);
    const from = blocks.findIndex((b) => b.id === dragId);
    const to = blocks.findIndex((b) => b.id === targetId);
    const next = [...blocks];
    next.splice(to, 0, next.splice(from, 1)[0]);
    persist(next);
    setOverId(null);
    setDragId(null);
  };

  const cycleSpan = (id) => persist(blocks.map((b) => (b.id === id ? { ...b, span: b.span === 3 ? 1 : b.span + 1 } : b)));

  const exportXlsx = async () => {
    try {
      const res = await apiClient.get("/performance/export", {
        params: { period, date, ...(serviceFilter !== "all" ? { service_id: serviceFilter } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `performance_${period}_${date}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Erreur pendant l'export"); }
  };

  const renderBlock = (id) => {
    switch (id) {
      case "avancement": return <BlockAvancement metrics={metrics} workdays={workdays} period={period} />;
      case "classement": return <BlockCollaborateurs ranking={ranking} />;
      case "podium": return <BlockPodium ranking={ranking} periodLabel={summary?.period_label} previousLabel={summary?.previous_label} complete={workdays.left === 0} />;
      case "champions": return <BlockChampions services={championsByService} previousLabel={summary?.previous_label} complete={workdays.left === 0} />;
      case "tableau": return <BlockTableau metrics={metrics} summary={summary} />;
      case "vigilance": return <BlockVigilance metrics={metrics} />;
      case "graphique":
        return (
          <>
            <div className="mb-3">
              <Select value={trendMetric} onValueChange={setTrendMetric}>
                <SelectTrigger className="h-9 rounded-sm border-zinc-300 w-full sm:w-[300px] text-xs" data-testid="perf-trend-select">
                  <SelectValue placeholder="Choisir un indicateur" />
                </SelectTrigger>
                <SelectContent>
                  {metrics.map((m) => (
                    <SelectItem key={m.metric_id} value={m.metric_id}>{m.service_name} — {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <BlockGraphique points={trend?.points} metricId={trendMetric} meta={trendMeta} dailyTarget={trendTargetDaily} />
          </>
        );
      default: return null;
    }
  };

  return (
    <>
      <Topbar
        title="Performance"
        subtitle="Jour, semaine ou mois — comparaison avec la période précédente et alertes par service"
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="inline-flex items-center border border-zinc-300 bg-white" data-testid="perf-period-nav">
              <button
                onClick={() => prevNav && setDate(prevNav.iso)}
                disabled={!prevNav}
                title={prevNav ? `Voir ${prevNav.label}` : ""}
                className="inline-flex items-center gap-1 h-9 px-2.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                data-testid="perf-nav-prev"
              >
                <CaretLeft size={13} weight="bold" />
                <span className="first-letter:uppercase hidden sm:inline">{prevNav?.label || "—"}</span>
              </button>
              <span className="w-px h-5 bg-zinc-200" />
              <button
                onClick={() => nextNav && setDate(nextNav.iso)}
                disabled={!nextNav}
                title={nextNav ? `Voir ${nextNav.label}` : `Déjà sur ${PERIOD_WORD[period]} en cours`}
                className="inline-flex items-center gap-1 h-9 px-2.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
                data-testid="perf-nav-next"
              >
                <span className="first-letter:uppercase hidden sm:inline">{nextNav?.label || `${PERIOD_WORD[period]} en cours`}</span>
                <CaretRight size={13} weight="bold" />
              </button>
            </div>
            {!atCurrent && (
              <button
                onClick={() => setDate(todayISO())}
                className="h-9 px-3 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50"
                data-testid="perf-nav-today"
              >
                Revenir à aujourd'hui
              </button>
            )}
            <div className="flex items-center gap-2 h-9 px-3 border border-zinc-300 bg-white">
              <CalendarBlank size={14} weight="bold" className="text-zinc-400" />
              <input
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="text-xs outline-none bg-transparent font-mono"
                data-testid="perf-date"
              />
            </div>
            <div className="inline-flex border border-zinc-300 bg-white" data-testid="perf-period-switch">
              {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setPeriod(id)}
                  data-testid={`perf-period-${id}`}
                  className={`px-4 h-9 text-xs transition-colors ${period === id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button onClick={exportXlsx} className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50" data-testid="perf-btn-export">
              <DownloadSimple size={15} weight="bold" /> Excel
            </button>
            <button onClick={resetLayout} className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50" data-testid="perf-btn-reset-layout">
              <ArrowsClockwise size={15} weight="bold" /> Disposition par défaut
            </button>
            {user?.role === "admin" && (
              <button onClick={publishLayout} className="inline-flex items-center gap-1.5 h-9 px-3 bg-zinc-900 text-white text-xs hover:bg-zinc-800" data-testid="perf-btn-publish-layout">
                <UploadSimple size={15} weight="bold" /> Publier pour tous
              </button>
            )}
          </div>
        }
      />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="perf-proposal">
        <div className="border border-zinc-200 bg-white px-4 py-2.5 mb-6 text-xs text-zinc-600 flex flex-wrap items-center gap-2" data-testid="perf-layout-banner">
          <span>Glissez les blocs par la poignée, cliquez sur leur largeur pour la changer.</span>
          <span className="text-zinc-400">
            {layoutSource === "user" ? "Disposition personnelle" : layoutSource === "default" ? "Disposition définie par l'administrateur" : "Disposition d'origine"}
          </span>
          <Link to="/performance/detail" className="underline font-medium ml-auto" data-testid="perf-link-classic">
            Vue détaillée jour / semaine / mois
          </Link>
        </div>

        {loading ? (
          <div className="bg-white border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-500">Chargement…</div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="h-9 rounded-sm border-zinc-300 w-full sm:w-[260px] text-xs" data-testid="perf-header-service-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les services</SelectItem>
                  {(summary?.services || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-zinc-500" data-testid="perf-range-info">
                <span className="text-zinc-700 font-medium">{summary.period_label}</span>
                <span className="font-mono text-zinc-500"> ({frDate(summary.range.start)} → {frDate(summary.range.end)})</span>
                <span className="mx-2 text-zinc-300">·</span>
                comparé à <span className="text-zinc-700 font-medium">{summary.previous_label}</span>
                <span className="mx-2 text-zinc-300">·</span>
                {workdays.done}/{workdays.total} jours ouvrés écoulés
                {summary.previous_days_with_entries === 0 && (
                  <span className="ml-2 text-amber-700 font-medium" data-testid="perf-prev-empty">— aucune donnée sur {summary.previous_label}</span>
                )}
              </div>
            </div>

            <div className="label-tiny text-zinc-400 mb-2">Statut par service — {summary.period_label}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mb-7" data-testid="perf-status-bar">
              {serviceStatus.filter((s) => serviceFilter === "all" || s.id === serviceFilter).map((s) => {
                const st = STATUS_STYLE[s.status];
                return (
                  <div key={s.id} className={`border border-zinc-200 border-l-4 ${st.bar} ${st.row} bg-white px-3 py-2.5`} data-testid={`perf-status-badge-${s.id}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                      <span className="text-xs font-medium truncate">{s.name}</span>
                    </div>
                    <div className={`text-[11px] mt-1 font-semibold ${st.text}`}>{st.label}</div>
                    <div className="label-tiny text-zinc-400 mt-0.5">{s.counted ? `${s.ok}/${s.counted} indicateurs au vert` : "pas d'objectif"}</div>
                  </div>
                );
              })}
            </div>

            {/* Blocs déplaçables */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 items-start">
              {blocks.map((b) => (
                <section
                  key={b.id}
                  onDragOver={(e) => { e.preventDefault(); setOverId(b.id); }}
                  onDrop={() => onDrop(b.id)}
                  className={`bg-white border ${overId === b.id && dragId !== b.id ? "border-dashed border-zinc-400 shadow-md" : "border-zinc-200"} ${dragId === b.id ? "opacity-50" : ""} ${b.span === 3 ? "md:col-span-3" : b.span === 2 ? "md:col-span-2" : "md:col-span-1"} transition-shadow`}
                  data-testid={`perf-section-${b.id}`}
                >
                  <header
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", b.id); e.dataTransfer.effectAllowed = "move"; setDragId(b.id); }}
                    onDragEnd={() => { setDragId(null); setOverId(null); }}
                    className="flex items-center justify-between gap-2 px-4 py-3 border-b border-zinc-100 cursor-grab active:cursor-grabbing"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <DotsSixVertical size={18} weight="bold" className="text-zinc-300 hover:text-zinc-600 cursor-grab shrink-0" data-testid={`perf-drag-handle-block-${b.id}`} />
                      <h2 className="font-heading text-base font-semibold tracking-tight truncate">
                        {b.id === "avancement" ? `Avancement ${PERIOD_LABEL_TITLE[period]} vs objectif` : BLOCK_META[b.id] || b.id}
                      </h2>
                    </div>
                    <button
                      onClick={() => cycleSpan(b.id)}
                      className="text-[10px] uppercase tracking-wide text-zinc-500 border border-zinc-200 px-1.5 py-0.5 hover:border-zinc-400 shrink-0"
                      title="Changer la largeur du bloc"
                      data-testid={`perf-block-size-${b.id}`}
                    >
                      {b.span === 3 ? "pleine largeur" : b.span === 2 ? "2/3" : "1/3"}
                    </button>
                  </header>
                  <div className="p-4">{renderBlock(b.id)}</div>
                </section>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] text-zinc-500" data-testid="perf-legend">
              <span className="font-semibold uppercase tracking-wide text-zinc-400">Code couleur</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-600" /> objectif tenu (≥ {thresholds.green_min} % du rythme attendu)</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-500" /> à surveiller ({thresholds.amber_min} – {thresholds.green_min} %)</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-rose-600" /> en alerte (&lt; {thresholds.amber_min} %)</span>
              <span className="text-zinc-400">— pour les indicateurs « plus bas = mieux », la logique est inversée (orange jusqu'à {thresholds.down_amber_max} % du seuil). Valeur absente = «&nbsp;—&nbsp;», jamais un zéro.</span>
            </div>
          </>
        )}
      </main>
    </>
  );
}
